import classNames from "classnames";
import * as React from "react";

type GeneratedListItemProps = {
  id: string;
  ref: React.RefObject<HTMLDivElement | HTMLUListElement | HTMLLIElement>;
  className: string;
  style: React.CSSProperties;
  onClick: (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
  role: string;
  "aria-label": string;
  "aria-selected": boolean;
};

export interface ListItemProps<T> {
  id: string;
  active: boolean;
  label: string;
  onClick: (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
  value: T;
  children: React.ReactNode;
  listBoxRef: React.RefObject<HTMLUListElement | HTMLDivElement>;
  className?: string;

  render?: (listItemProps: GeneratedListItemProps) => JSX.Element;
}

export const ListItem = <T,>({
  id,
  active,
  label,
  value,
  onClick,
  children,
  listBoxRef,
  className,

  render,
}: ListItemProps<T>): JSX.Element => {
  const listRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    if (!active) {
      return;
    }

    if (listRef.current && listBoxRef.current) {
      const parentNode = listBoxRef.current;
      const currentNode = listRef.current;

      /* - delta */
      if (currentNode.offsetTop < parentNode.scrollTop) {
        // scrollIntoView with top aligned
        currentNode.scrollIntoView({ block: "nearest" });
        // parentNode.scrollTop = currentNode.offsetTop;
      }

      /* + delta */
      if (
        currentNode.offsetTop + currentNode.clientHeight >
        parentNode.scrollTop + parentNode.clientHeight
      ) {
        // scrollIntoView with bottom aligned
        currentNode.scrollIntoView({ block: "nearest" });
      }
    }
  }, [active, value, listBoxRef]);

  const listItemProps: GeneratedListItemProps = {
    id,
    ref: listRef,
    className: classNames("dropdown-item text-truncate px-3", className, {
      active: active,
    }),
    style: {
      cursor: "pointer",
    },
    onClick,
    role: "option",
    "aria-label": label,
    "aria-selected": active,
  };

  if (render) {
    return render(listItemProps);
  }

  const { ref, ...restProps } = listItemProps;

  return (
    <li ref={ref as React.Ref<HTMLLIElement>} {...restProps}>
      {children}
    </li>
  );
};
